import type { LiveBreakHost } from "@hobbyshopify/backend-client";
import { Link } from "react-router-dom";
import { useObserver, useSubtree } from "subtree.js/react";
import { LoadFailed, Loading } from "../../components/LoadState";
import { ShareButton } from "../../components/ShareButton";
import { formatBreakTime } from "../../core/primitives/lineup";
import { breakBadge, formatCents, liveBadge } from "../../core/primitives/live";
import type { ShareTarget } from "../../core/primitives/share-links";
import { LineupPicker } from "../../sections/lineup-picker/lineup-picker";
import { fillBadge, LiveStreamsActions, LiveStreamsState } from "./live-streams-model";

/**
 * The Live page: the shop's open streams, then its boxes — the ones still to
 * be broken, then the ones already broken. Go live opens a stream with a
 * line-up; the cockpit is where boxes go on screen and get broken.
 */

const isDone = (b: LiveBreakHost) => b.status === "COMPLETED" || b.status === "CANCELLED";

function BreakRow({ breakRow, share }: { breakRow: LiveBreakHost; share: ShareTarget | undefined }) {
  const selling = breakRow.status === "ON_SALE";
  const badge = selling ? fillBadge(breakRow.soldCount, breakRow.slotCount) : breakBadge(breakRow.status);
  const pct = breakRow.slotCount === 0 ? 0 : Math.round((breakRow.soldCount / breakRow.slotCount) * 100);
  const left = Math.max(0, breakRow.slotCount - breakRow.soldCount);
  const done = isDone(breakRow);

  const body = (
    <>
      {breakRow.imageUrl ? (
        <img src={breakRow.imageUrl} alt="" className="h-16 w-12 shrink-0 rounded object-cover" />
      ) : (
        <div className="h-16 w-12 shrink-0 rounded bg-white/5" />
      )}
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <span className={`badge shrink-0 ${badge.cls}`}>{badge.label}</span>
          <span className="truncate font-medium">{breakRow.title}</span>
          {!done && breakRow.segment && <span className="badge badge-neutral shrink-0">In a line-up</span>}
        </div>
        <div className="text-sm opacity-60">
          {breakRow.soldCount}/{breakRow.slotCount} spots · {formatCents(breakRow.grossSoldCents)} taken
          {selling && left > 0 && ` · ${left} to go`}
          {!done && breakRow.breakNotBefore && ` · breaks ${formatBreakTime(breakRow.breakNotBefore)}`}
        </div>
        {!done && (
          <div className="flex items-center gap-2">
            <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-white/10">
              <div className="h-full rounded-full bg-[color:var(--color-accent)]" style={{ width: `${pct}%` }} />
            </div>
            <span className="shrink-0 text-xs tabular-nums opacity-60">{pct}%</span>
          </div>
        )}
      </div>
    </>
  );

  // A box in a line-up opens that cockpit; one still on the shelf opens its own page.
  return (
    <div className="panel flex items-center gap-2 p-3 transition hover:brightness-110">
      <Link
        to={breakRow.segment && !done ? `/live/streams/${breakRow.segment.streamId}` : `/live/breaks/${breakRow.id}`}
        className="flex min-w-0 flex-1 items-center gap-3"
      >
        {body}
      </Link>
      {share && (
        <ShareButton
          target={share}
          heading={breakRow.status === "COMPLETED" ? "Share the results" : "Share this break"}
          note="Buyers grab a spot on hobbyshop.ai or in the HobbyScan app. It fills faster when it is shared."
          iconOnly
          className="btn-ghost btn-sm shrink-0"
        />
      )}
    </div>
  );
}

export function LiveStreamsPage() {
  const state = useSubtree(LiveStreamsState);
  const actions = useSubtree(LiveStreamsActions);
  const observer = useObserver();
  const groups = observer.watch(state.groups);
  const breakShares = observer.watch(state.breakShares);
  const queue = observer.watch(state.queue);
  const queueStatus = observer.watch(state.queueStatus);
  const goLive = observer.watch(state.goLive);
  const openStreams = observer.watch(state.open);

  const loading = queueStatus.kind === "loading" && queue.length === 0;
  const onAir = groups.live.length > 0;
  const open = queue.filter((b) => !isDone(b));
  const broken = queue.filter(isDone);

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Live breaks</h1>
          <p className="text-sm opacity-60">
            Add break inventory, go live with a line-up, break them on screen. Ship from Shipping.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/live/sell" className="btn-secondary">
            Break inventory
          </Link>
          <button type="button" className="btn-primary" onClick={actions.goLive}>
            {onAir ? "Back to the stream" : "Go live"}
          </button>
        </div>
      </header>

      {goLive.error && <p className="note note-danger">{goLive.error}</p>}

      {openStreams.length > 0 && (
        <section className="space-y-2">
          <h2 className="eyebrow">Open now</h2>
          {openStreams.map((stream) => {
            const badge = liveBadge(stream);
            return (
              <Link
                key={stream.id}
                to={`/live/streams/${stream.id}`}
                className="panel flex items-center gap-3 p-3 transition hover:brightness-110"
              >
                <span className={`badge shrink-0 ${badge.cls}`}>{badge.label}</span>
                <span className="min-w-0 flex-1 truncate font-medium">{stream.title}</span>
                <span className="shrink-0 text-sm opacity-60">
                  {stream.host ? `hosted by ${stream.host.name}` : ""} · {stream.segments.length} in line-up
                </span>
              </Link>
            );
          })}
        </section>
      )}

      <LineupPicker />
      {queueStatus.kind === "error" && (
        <LoadFailed
          title="Couldn't load your break inventory."
          message={queueStatus.message}
          onRetry={() => void actions.reload()}
        />
      )}
      {loading && <Loading label="Loading your boxes…" />}

      {!loading && open.length === 0 && queueStatus.kind !== "error" && (
        <div className="panel p-6 text-center">
          <p className="font-medium">Nothing on sale</p>
          <p className="mt-1 text-sm opacity-60">
            Add your break inventory. Spots sell from here before any stream; when you go live, you pin a box and break
            it.
          </p>
          <Link to="/live/sell" className="btn-primary mt-4">
            Add your first break inventory
          </Link>
        </div>
      )}

      {open.length > 0 && (
        <section className="space-y-2">
          <h2 className="eyebrow">Not broken yet</h2>
          {open.map((breakRow) => (
            <BreakRow key={breakRow.id} breakRow={breakRow} share={breakShares.get(breakRow.id)} />
          ))}
        </section>
      )}

      {broken.length > 0 && (
        <section className="space-y-2">
          <h2 className="eyebrow">Broken</h2>
          {broken.map((breakRow) => (
            <BreakRow key={breakRow.id} breakRow={breakRow} share={breakShares.get(breakRow.id)} />
          ))}
        </section>
      )}
    </div>
  );
}
